import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import RegisterModal from '@/components/feature/RegisterModal';
import LoginModal from '@/components/feature/LoginModal';
import { useAuth } from '@/context/AuthContext';

const navLinks = [
  { path: '/', label: 'Inicio', icon: 'ri-home-4-line' },
  { path: '/estadisticas', label: 'Estadísticas', icon: 'ri-bar-chart-2-line' },
  { path: '/clasificacion', label: 'Clasificación', icon: 'ri-list-ordered' },
  { path: '/comunidad', label: 'Comunidad/Blogs', icon: 'ri-group-line' },
  { path: '/ligas', label: 'Ligas', icon: 'ri-trophy-line' },
  { path: '/ranking', label: 'Ranking', icon: 'ri-medal-line' },
  { path: '/comparador', label: 'Comparador', icon: 'ri-scales-3-line' },
  { path: '/perfiles', label: 'Perfiles', icon: 'ri-user-search-line' },
];

const resourceLinks = [
  { path: '/guia', label: 'Guía del Apostador', icon: 'ri-book-open-line', desc: 'Estrategias, bankroll y más' },
  { path: '/juego-responsable', label: 'Juego Responsable', icon: 'ri-shield-check-line', desc: 'Ayuda y recursos FEJAR' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const resourcesRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { isRegistered, user, plan, registerModalOpen, openRegisterModal, closeRegisterModal, loginModalOpen, openLoginModal, closeLoginModal, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setResourcesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (resourcesRef.current && !resourcesRef.current.contains(e.target as Node)) {
        setResourcesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const isResourcesActive = resourceLinks.some((r) => isActive(r.path));

  const planBadge = plan === 'elite'
    ? { label: 'ELITE', color: '#ffd700', bg: 'bg-[#ffd700]/10' }
    : plan === 'premium'
    ? { label: 'PREMIUM', color: '#00e87a', bg: 'bg-[#00e87a]/10' }
    : { label: 'FREE', color: '#a8b2c1', bg: 'bg-white/5' };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#0d0f14]/95 backdrop-blur-md border-b border-[#2a2d3a]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 flex-shrink-0">
          <img
            src="https://storage.readdy-site.link/project_files/fb63bf06-0e48-4930-a6dd-45971086fa42/2303f7d2-fc6e-41fc-94be-8137f8672a38_Gemini_Generated_Image_ttooeyttooeyttoo-1.png?v=65c53162ba716b86e52e0cd98ba851d4"
            alt="Apuestamente"
            className="h-9 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                isActive(link.path)
                  ? 'bg-[#00e87a]/15 text-[#00e87a]'
                  : 'text-[#a8b2c1] hover:text-white hover:bg-white/5'
              }`}
            >
              {link.label}
            </Link>
          ))}

          {/* Recursos dropdown */}
          <div className="relative" ref={resourcesRef}>
            <button
              onClick={() => setResourcesOpen((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                isResourcesActive
                  ? 'bg-[#00e87a]/15 text-[#00e87a]'
                  : 'text-[#a8b2c1] hover:text-white hover:bg-white/5'
              }`}
            >
              Recursos
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${resourcesOpen ? 'rotate-180' : ''}`}></i>
              </div>
            </button>

            {resourcesOpen && (
              <div className="absolute top-full left-0 mt-2 w-64 bg-[#0d0f14] border border-[#2a2d3a] rounded-xl overflow-hidden shadow-xl">
                {resourceLinks.map((r) => (
                  <Link
                    key={r.path}
                    to={r.path}
                    className={`flex items-start gap-3 px-4 py-3.5 hover:bg-white/5 transition-colors cursor-pointer ${
                      isActive(r.path) ? 'bg-[#00e87a]/5' : ''
                    }`}
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5 ${
                      isActive(r.path) ? 'bg-[#00e87a]/15' : 'bg-white/5'
                    }`}>
                      <i className={`${r.icon} text-sm ${isActive(r.path) ? 'text-[#00e87a]' : 'text-[#6b7280]'}`}></i>
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${isActive(r.path) ? 'text-[#00e87a]' : 'text-white'}`}>{r.label}</p>
                      <p className="text-[#6b7280] text-xs mt-0.5">{r.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Side */}
        <div className="hidden md:flex items-center gap-3">
          {isRegistered ? (
            <>
              <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-widest ${planBadge.bg}`} style={{ color: planBadge.color }}>
                {planBadge.label}
              </span>
              <Link
                to="/perfil"
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer ${
                  isActive('/perfil')
                    ? 'bg-[#00e87a]/15 text-[#00e87a]'
                    : 'text-[#a8b2c1] hover:text-white hover:bg-white/5'
                }`}
              >
                <div className="w-6 h-6 flex items-center justify-center bg-[#00e87a]/15 rounded-full text-[#00e87a]">
                  <i className="ri-user-line text-sm"></i>
                </div>
                <span className="text-sm font-medium whitespace-nowrap">@{user?.username}</span>
              </Link>
              <button
                onClick={logout}
                className="px-3 py-2 text-[#6b7280] text-sm hover:text-white hover:bg-white/5 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                Salir
              </button>
            </>
          ) : (
            <>
              <button
                onClick={openLoginModal}
                className="px-4 py-2 text-[#a8b2c1] text-sm font-medium hover:text-white hover:bg-white/5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                Iniciar sesión
              </button>
              <button
                onClick={openRegisterModal}
                className="px-4 py-2 bg-[#00e87a] text-black text-sm font-bold rounded-md hover:bg-[#00d070] transition-colors whitespace-nowrap cursor-pointer"
              >
                Registrarse
              </button>
            </>
          )}
        </div>

        {/* Mobile Toggle */}
        <button
          className="md:hidden w-8 h-8 flex items-center justify-center text-white cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-[#0d0f14]/98 backdrop-blur-md border-b border-[#2a2d3a] px-4 pb-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 text-sm font-medium transition-all cursor-pointer ${
                isActive(link.path)
                  ? 'bg-[#00e87a]/15 text-[#00e87a]'
                  : 'text-[#a8b2c1] hover:text-white hover:bg-white/5'
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${link.icon} text-base`}></i>
              </div>
              {link.label}
            </Link>
          ))}

          {/* Resources in mobile */}
          <div className="mt-1 mb-1">
            <p className="px-4 py-2 text-[#4b5563] text-xs font-semibold uppercase tracking-widest">Recursos</p>
            {resourceLinks.map((r) => (
              <Link
                key={r.path}
                to={r.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 text-sm font-medium transition-all cursor-pointer ${
                  isActive(r.path)
                    ? 'bg-[#00e87a]/15 text-[#00e87a]'
                    : 'text-[#a8b2c1] hover:text-white hover:bg-white/5'
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${r.icon} text-base`}></i>
                </div>
                {r.label}
              </Link>
            ))}
          </div>

          <div className="pt-3 border-t border-[#2a2d3a] flex flex-col gap-2">
            {isRegistered ? (
              <>
                <Link to="/perfil" className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-[#a8b2c1] hover:text-white cursor-pointer">
                  <div className="w-5 h-5 flex items-center justify-center"><i className="ri-user-line text-base"></i></div>
                  @{user?.username}
                </Link>
                <button
                  onClick={logout}
                  className="w-full px-4 py-3 border border-[#2a2d3a] text-[#a8b2c1] text-sm font-semibold rounded-md cursor-pointer whitespace-nowrap"
                >
                  Cerrar sesión
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => { setMobileOpen(false); openLoginModal(); }}
                  className="w-full px-4 py-3 border border-[#2a2d3a] text-[#a8b2c1] text-sm font-semibold rounded-md cursor-pointer whitespace-nowrap"
                >
                  Iniciar sesión
                </button>
                <button
                  onClick={() => { setMobileOpen(false); openRegisterModal(); }}
                  className="w-full px-4 py-3 bg-[#00e87a] text-black text-sm font-bold rounded-md cursor-pointer whitespace-nowrap"
                >
                  Registrarse Gratis
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <RegisterModal isOpen={registerModalOpen} onClose={closeRegisterModal} />
      <LoginModal isOpen={loginModalOpen} onClose={closeLoginModal} onSwitchToRegister={openRegisterModal} />
    </nav>
  );
}
